import {
  formatSeatCode,
  formatSeatFullAccess,
  formatTableSeatLabel,
  getPositions,
} from '../lib/poker'
import type { StackUnit } from '../lib/types'
import { t } from '../i18n/strings'
import { el } from './dom'

export type TableVizMode = 'heroPick' | 'holePick' | 'preview'

/** Which center/hero compact editor is open (module-ephemeral across re-renders). */
export type TableInfoEdit = 'blinds' | 'pot' | 'stack' | null

let activeInfoEdit: TableInfoEdit = null

export function getActiveInfoEdit(): TableInfoEdit {
  return activeInfoEdit
}

export function clearActiveInfoEdit(): void {
  activeInfoEdit = null
}

/** Common blind presets for compact ± cycling (SB/BB). */
export const BLIND_PRESETS: ReadonlyArray<{ sb: number; bb: number }> = [
  { sb: 0.25, bb: 0.5 },
  { sb: 0.5, bb: 1 },
  { sb: 1, bb: 2 },
  { sb: 2, bb: 5 },
  { sb: 5, bb: 10 },
  { sb: 10, bb: 20 },
  { sb: 25, bb: 50 },
]

/** Stack depth stops (bb) for compact hero-stack stepper. */
export const STACK_BB_STOPS_VIZ: readonly number[] = [
  10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 100, 120, 150, 200, 250, 300,
]

/** Pot (bb) stops for compact pot stepper; null = unset. */
export const POT_BB_STOPS_VIZ: readonly number[] = [
  1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50,
]

export type TablePlayInfo = {
  smallBlind: number
  bigBlind: number
  potBb?: number
  heroStackBb: number
  heroStackChips: number
  stackUnit: StackUnit
  /** Global effective stack — center hint only, not per-seat. */
  effectiveStackBb?: number
}

export type TableVizOptions = {
  playerCount: number
  heroPosition: string
  mode: TableVizMode
  /** Active hole-entry seat: 'hero' or a position code. */
  activeSeat?: 'hero' | string
  /** Positions (not including hero) that have at least one hole card. */
  filledVillainSeats?: ReadonlySet<string>
  heroFilled?: boolean
  /** Compact / preview sizing (Table + Hand mini). */
  compact?: boolean
  onSelectSeat?: (pos: string) => void
  /**
   * Blinds / pot / hero stack for center + Hero badge.
   * When omitted, viz stays seat-only (legacy).
   */
  playInfo?: TablePlayInfo
  /**
   * Enable tap-to-edit compact steppers for blinds / pot / hero stack.
   * Requires playInfo + matching setters. Seat taps unchanged.
   */
  infoEditable?: boolean
  onSetBlinds?: (sb: number, bb: number) => void
  onSetPotBb?: (potBb: number | null) => void
  onSetHeroStackBb?: (bb: number) => void
  onSetHeroStackChips?: (chips: number) => void
  /** Force a UI re-render after toggling ephemeral edit focus. */
  onInfoEditChange?: (edit: TableInfoEdit) => void
}

/** Felt / track silhouette by player count. */
export type TableShapeKind = 'ellipse' | 'stadiumSoft' | 'stadium'

export function tableShapeKind(playerCount: number): TableShapeKind {
  const n = Math.min(9, Math.max(2, playerCount))
  if (n <= 3) return 'ellipse'
  if (n <= 6) return 'stadiumSoft'
  return 'stadium'
}

/**
 * Seat-center track in % of the `.tableViz` box.
 * `r` is the stadium end-cap radius (unused for pure ellipse placement).
 */
export function tableTrackGeom(playerCount: number): {
  kind: TableShapeKind
  rx: number
  ry: number
  r: number
} {
  const kind = tableShapeKind(playerCount)
  if (kind === 'ellipse') {
    // Compact oval — HU–3 stay cozy; seats just outside the felt.
    return { kind, rx: 38, ry: 40, r: 38 }
  }
  if (kind === 'stadiumSoft') {
    // Soft capsule: short straight rails, still rounded overall.
    return { kind, rx: 36, ry: 42, r: 36 }
  }
  // Pronounced vertical stadium — long straight side rails for 7–9.
  return { kind, rx: 34, ry: 43, r: 34 }
}

/** Clockwise seat order starting at BTN (table ring order, independent of view). */
export function seatsClockwiseFromBtn(playerCount: number): string[] {
  const positions = getPositions(playerCount)
  const btnIdx = positions.indexOf('BTN')
  if (btnIdx < 0) return positions
  return [...positions.slice(btnIdx), ...positions.slice(0, btnIdx)]
}

/**
 * Clockwise seat order starting at Hero — used so Hero sits at south (bottom).
 * Preserves relative poker seating; dealer pip still marks BTN wherever it lands.
 */
export function seatsClockwiseFromHero(
  playerCount: number,
  heroPosition: string,
): string[] {
  const fromBtn = seatsClockwiseFromBtn(playerCount)
  const heroIdx = fromBtn.indexOf(heroPosition)
  if (heroIdx < 0) return fromBtn
  return [...fromBtn.slice(heroIdx), ...fromBtn.slice(0, heroIdx)]
}

/**
 * Point on a vertical stadium / capsule perimeter.
 * t=0 → bottom center; t increases clockwise (toward screen-left first).
 * Coords are % for CSS `left` / `top` (seat center).
 */
export function pointOnStadiumPercents(
  rx: number,
  ry: number,
  r: number,
  t: number,
): { left: number; top: number } {
  const rad = Math.min(r, rx, ry)
  const straight = 2 * Math.max(0, ry - rad)
  const quarter = (Math.PI / 2) * rad
  const halfArc = Math.PI * rad
  const perimeter = 2 * halfArc + 2 * straight
  // Degenerate → circle
  const len = perimeter > 0 ? perimeter : 2 * Math.PI * Math.max(rad, 1)
  let s = ((((t % 1) + 1) % 1) * len)

  const cx = 50
  const cy = 50
  const cyB = cy + ry - rad
  const cyT = cy - ry + rad

  // 1) Bottom-left quarter: θ π/2 → π
  if (s <= quarter || straight + halfArc === 0) {
    const u = quarter > 0 ? Math.min(s, quarter) / quarter : 0
    const theta = Math.PI / 2 + u * (Math.PI / 2)
    return {
      left: cx + rad * Math.cos(theta),
      top: cyB + rad * Math.sin(theta),
    }
  }
  s -= quarter

  // 2) Left straight upward (y decreases)
  if (s <= straight) {
    return { left: cx - rad, top: cyB - s }
  }
  s -= straight

  // 3) Top semicircle: θ π → 2π
  if (s <= halfArc) {
    const u = halfArc > 0 ? s / halfArc : 0
    const theta = Math.PI + u * Math.PI
    return {
      left: cx + rad * Math.cos(theta),
      top: cyT + rad * Math.sin(theta),
    }
  }
  s -= halfArc

  // 4) Right straight downward (y increases)
  if (s <= straight) {
    return { left: cx + rad, top: cyT + s }
  }
  s -= straight

  // 5) Bottom-right quarter: θ 0 → π/2
  const u = quarter > 0 ? s / quarter : 0
  const theta = u * (Math.PI / 2)
  return {
    left: cx + rad * Math.cos(theta),
    top: cyB + rad * Math.sin(theta),
  }
}

/**
 * Place seats on the track matching the felt silhouette for N.
 * Hero at bottom (south); then clockwise from Hero’s view. Ellipse for HU–3;
 * stadium perimeter (even arc-length, including straight side rails) for 4+.
 */
export function seatLayoutPercents(
  playerCount: number,
  heroPosition: string,
): { pos: string; left: number; top: number }[] {
  const seats = seatsClockwiseFromHero(playerCount, heroPosition)
  const n = seats.length
  const geom = tableTrackGeom(playerCount)

  if (geom.kind === 'ellipse') {
    const { rx, ry } = geom
    return seats.map((pos, i) => {
      // θ=π/2 → bottom (Hero); increase θ → clockwise in CSS (y grows down).
      const theta = Math.PI / 2 + (2 * Math.PI * i) / n
      const left = 50 + rx * Math.cos(theta)
      const top = 50 + ry * Math.sin(theta)
      return { pos, left, top }
    })
  }

  const { rx, ry, r } = geom
  return seats.map((pos, i) => {
    const { left, top } = pointOnStadiumPercents(rx, ry, r, i / n)
    return { pos, left, top }
  })
}

function isInteractive(mode: TableVizMode): boolean {
  return mode === 'heroPick' || mode === 'holePick'
}

/** Trim trailing zeros for short numeric labels. */
export function formatVizNum(n: number): string {
  if (!Number.isFinite(n)) return '—'
  const rounded = Math.round(n * 100) / 100
  if (Number.isInteger(rounded)) return String(rounded)
  return String(rounded)
    .replace(/(\.[0-9]*?)0+$/, '$1')
    .replace(/\.$/, '')
}

/** Short blinds label for table center, e.g. `0.5/1`. */
export function formatBlindsShort(sb: number, bb: number): string {
  return `${formatVizNum(sb)}/${formatVizNum(bb)}`
}

/** Hero stack badge: `100bb` or chip amount when unit is chips. */
export function formatHeroStackShort(
  heroStackBb: number,
  heroStackChips: number,
  stackUnit: StackUnit,
): string {
  if (stackUnit === 'chips') {
    return formatVizNum(heroStackChips)
  }
  return `${formatVizNum(heroStackBb)}bb`
}

/**
 * Pot label for center. Returns null when unset → soft placeholder in UI.
 */
export function formatPotShort(potBb: number | undefined | null): string | null {
  if (potBb == null || !Number.isFinite(potBb) || potBb < 0) return null
  return `${formatVizNum(potBb)}bb`
}

/**
 * Center pot chip text value (without leading "Pot "): `12bb`, else null.
 * Softwise M10d: amount only — no HU / N-way shape token.
 */
export function formatPotCenterValue(
  potBb: number | undefined | null,
): string | null {
  return formatPotShort(potBb) || null
}

/** UI bits needed to build the live table-center pot chip label. */
export type TableCenterPotUi = {
  tableVizPotValue: (v: string) => string
  tableVizPotPlaceholder: string
}

/**
 * Full center pot chip label — never double-prefix "Pot".
 * - amount → tableVizPotValue(`12bb`)
 * - none → placeholder (`Pot`) as-is (do not wrap again)
 */
export function formatTableCenterPotChipText(
  potBb: number | undefined | null,
  ui: TableCenterPotUi,
): string {
  const potLabel = formatPotCenterValue(potBb ?? null)
  if (potLabel) return ui.tableVizPotValue(potLabel)
  return ui.tableVizPotPlaceholder
}

/** Nearest index in a numeric stop list. */
export function nearestStopIndex(stops: readonly number[], value: number): number {
  let best = 0
  let bestDist = Infinity
  for (let i = 0; i < stops.length; i++) {
    const d = Math.abs(stops[i]! - value)
    if (d < bestDist) {
      bestDist = d
      best = i
    }
  }
  return best
}

/** Step along stops; clamps at ends. */
export function stepAlongStops(
  stops: readonly number[],
  value: number,
  dir: -1 | 1,
): number {
  if (stops.length === 0) return value
  const idx = nearestStopIndex(stops, value)
  const exact = Math.abs(stops[idx]! - value) < 1e-9
  if (!exact && dir > 0 && value < stops[idx]!) return stops[idx]!
  if (!exact && dir < 0 && value > stops[idx]!) return stops[idx]!
  const next = Math.min(stops.length - 1, Math.max(0, idx + dir))
  return stops[next]!
}

/**
 * Cycle blind presets. Non-matching current → snap to nearest BB then step.
 */
export function stepBlindPreset(
  sb: number,
  bb: number,
  dir: -1 | 1,
): { sb: number; bb: number } {
  const exact = BLIND_PRESETS.findIndex(
    (p) => Math.abs(p.sb - sb) < 1e-9 && Math.abs(p.bb - bb) < 1e-9,
  )
  if (exact >= 0) {
    const next = Math.min(
      BLIND_PRESETS.length - 1,
      Math.max(0, exact + dir),
    )
    return { ...BLIND_PRESETS[next]! }
  }
  const byBb = nearestStopIndex(
    BLIND_PRESETS.map((p) => p.bb),
    Number.isFinite(bb) ? bb : 1,
  )
  let idx = byBb
  if (dir > 0 && bb > BLIND_PRESETS[byBb]!.bb + 1e-9) {
    idx = Math.min(BLIND_PRESETS.length - 1, byBb + 1)
  } else if (dir < 0 && bb < BLIND_PRESETS[byBb]!.bb - 1e-9) {
    idx = Math.max(0, byBb - 1)
  } else {
    idx = Math.min(BLIND_PRESETS.length - 1, Math.max(0, byBb + dir))
  }
  return { ...BLIND_PRESETS[idx]! }
}

/**
 * Pot stepper: includes "unset" as a virtual stop before the first value.
 * dir −1 from 1bb (or nearest) can clear; dir +1 from unset → 1bb.
 */
export function stepPotBb(
  potBb: number | undefined | null,
  dir: -1 | 1,
): number | null {
  const unset = potBb == null || !Number.isFinite(potBb)
  if (unset) {
    return dir > 0 ? POT_BB_STOPS_VIZ[0]! : null
  }
  const v = potBb as number
  const idx = nearestStopIndex(POT_BB_STOPS_VIZ, v)
  const exact = Math.abs(POT_BB_STOPS_VIZ[idx]! - v) < 1e-9
  if (dir < 0) {
    if (!exact && v > POT_BB_STOPS_VIZ[idx]!) return POT_BB_STOPS_VIZ[idx]!
    if (idx <= 0) return null
    return POT_BB_STOPS_VIZ[idx - 1]!
  }
  if (!exact && v < POT_BB_STOPS_VIZ[idx]!) return POT_BB_STOPS_VIZ[idx]!
  if (idx >= POT_BB_STOPS_VIZ.length - 1) return POT_BB_STOPS_VIZ[idx]!
  return POT_BB_STOPS_VIZ[idx + 1]!
}

function toggleInfoEdit(
  kind: Exclude<TableInfoEdit, null>,
  onInfoEditChange?: (edit: TableInfoEdit) => void,
): void {
  activeInfoEdit = activeInfoEdit === kind ? null : kind
  onInfoEditChange?.(activeInfoEdit)
}

function closeInfoEdit(onInfoEditChange?: (edit: TableInfoEdit) => void): void {
  if (activeInfoEdit == null) return
  activeInfoEdit = null
  onInfoEditChange?.(null)
}

function stepBtn(
  label: string,
  aria: string,
  onClick: () => void,
): HTMLElement {
  return el('button', {
    className: 'tableInfoStepBtn',
    text: label,
    attrs: {
      type: 'button',
      'aria-label': aria,
    },
    on: { click: (e) => {
      e.stopPropagation()
      onClick()
    } },
  })
}

function renderCenterCluster(
  playInfo: TablePlayInfo,
  editable: boolean,
  opts: Pick<
    TableVizOptions,
    | 'onSetBlinds'
    | 'onSetPotBb'
    | 'onSetHeroStackBb'
    | 'onSetHeroStackChips'
    | 'onInfoEditChange'
  >,
): HTMLElement {
  const ui = t()
  const {
    smallBlind: sb,
    bigBlind: bb,
    potBb,
    effectiveStackBb,
  } = playInfo
  const blindsLabel = formatBlindsShort(sb, bb)
  const hasPotAmount = formatPotCenterValue(potBb ?? null) != null
  // Full chip copy once — reuse for text/title/aria (never re-wrap with Pot …).
  const potText = formatTableCenterPotChipText(potBb ?? null, ui)
  const editing = editable ? activeInfoEdit : null

  const blindsDisplay = editable
    ? el('button', {
        className: [
          'tableCenterChip',
          'tableCenterBlinds',
          editing === 'blinds' ? 'tableCenterChipActive' : '',
        ]
          .filter(Boolean)
          .join(' '),
        text: blindsLabel,
        attrs: {
          type: 'button',
          'aria-label': ui.tableVizBlindsAria(blindsLabel),
          'aria-expanded': editing === 'blinds' ? 'true' : 'false',
          title: ui.tableVizBlindsAria(blindsLabel),
        },
        on: {
          click: (e) => {
            e.stopPropagation()
            toggleInfoEdit('blinds', opts.onInfoEditChange)
          },
        },
      })
    : el('div', {
        className: 'tableCenterChip tableCenterBlinds tableCenterChipStatic',
        text: blindsLabel,
        attrs: {
          'aria-label': ui.tableVizBlindsAria(blindsLabel),
          title: blindsLabel,
        },
      })

  // Soft dashed chip when amount unset (placeholder or shape-only) so tap-to-set-bb stays discoverable.
  const potSoft = !hasPotAmount
  const potDisplay = editable
    ? el('button', {
        className: [
          'tableCenterChip',
          'tableCenterPot',
          potSoft ? 'tableCenterPotSoft' : '',
          editing === 'pot' ? 'tableCenterChipActive' : '',
        ]
          .filter(Boolean)
          .join(' '),
        text: potText,
        attrs: {
          type: 'button',
          'aria-label': potText,
          'aria-expanded': editing === 'pot' ? 'true' : 'false',
          title: potText,
        },
        on: {
          click: (e) => {
            e.stopPropagation()
            toggleInfoEdit('pot', opts.onInfoEditChange)
          },
        },
      })
    : el('div', {
        className: [
          'tableCenterChip',
          'tableCenterPot',
          'tableCenterChipStatic',
          potSoft ? 'tableCenterPotSoft' : '',
        ]
          .filter(Boolean)
          .join(' '),
        text: potText,
        attrs: {
          'aria-label': potText,
          title: potText,
        },
      })

  const kids: HTMLElement[] = [blindsDisplay, potDisplay]

  if (
    effectiveStackBb != null &&
    Number.isFinite(effectiveStackBb) &&
    effectiveStackBb >= 0
  ) {
    kids.push(
      el('div', {
        className: 'tableCenterEff',
        text: ui.tableVizEffHint(formatVizNum(effectiveStackBb)),
        attrs: {
          title: ui.tableVizEffTitle,
          'aria-label': ui.tableVizEffTitle,
        },
      }),
    )
  }

  const cluster = el(
    'div',
    { className: 'tableCenterCluster', attrs: { 'aria-hidden': editable ? undefined : 'true' } },
    kids,
  )

  let editor: HTMLElement | null = null
  if (editable && editing === 'blinds' && opts.onSetBlinds) {
    editor = el('div', { className: 'tableInfoEditor', attrs: { role: 'group' } }, [
      stepBtn('−', ui.tableVizStepDown, () => {
        const next = stepBlindPreset(sb, bb, -1)
        opts.onSetBlinds?.(next.sb, next.bb)
      }),
      el('span', {
        className: 'tableInfoEditorValue',
        text: blindsLabel,
        attrs: { 'aria-live': 'polite' },
      }),
      stepBtn('+', ui.tableVizStepUp, () => {
        const next = stepBlindPreset(sb, bb, 1)
        opts.onSetBlinds?.(next.sb, next.bb)
      }),
      el('button', {
        className: 'tableInfoDoneBtn',
        text: ui.tableVizEditDone,
        attrs: { type: 'button' },
        on: {
          click: (e) => {
            e.stopPropagation()
            closeInfoEdit(opts.onInfoEditChange)
          },
        },
      }),
    ])
  } else if (editable && editing === 'pot' && opts.onSetPotBb) {
    editor = el('div', { className: 'tableInfoEditor', attrs: { role: 'group' } }, [
      stepBtn('−', ui.tableVizStepDown, () => {
        opts.onSetPotBb?.(stepPotBb(potBb, -1))
      }),
      el('span', {
        className: 'tableInfoEditorValue',
        text: formatPotCenterValue(potBb ?? null) ?? ui.tableVizPotPlaceholder,
        attrs: { 'aria-live': 'polite' },
      }),
      stepBtn('+', ui.tableVizStepUp, () => {
        opts.onSetPotBb?.(stepPotBb(potBb, 1))
      }),
      el('button', {
        className: 'tableInfoDoneBtn',
        text: ui.tableVizEditDone,
        attrs: { type: 'button' },
        on: {
          click: (e) => {
            e.stopPropagation()
            closeInfoEdit(opts.onInfoEditChange)
          },
        },
      }),
    ])
  } else if (editable && editing === 'stack') {
    const stackLabel = formatHeroStackShort(
      playInfo.heroStackBb,
      playInfo.heroStackChips,
      playInfo.stackUnit,
    )
    editor = el('div', { className: 'tableInfoEditor', attrs: { role: 'group' } }, [
      stepBtn('−', ui.tableVizStepDown, () => {
        if (playInfo.stackUnit === 'chips') {
          const bbStop = stepAlongStops(
            STACK_BB_STOPS_VIZ,
            playInfo.heroStackBb,
            -1,
          )
          // Keep chips ↔ bb via bb step when BB known; else nudge chips.
          if (Number.isFinite(playInfo.bigBlind) && playInfo.bigBlind > 0) {
            opts.onSetHeroStackBb?.(bbStop)
          } else {
            opts.onSetHeroStackChips?.(
              Math.max(0, playInfo.heroStackChips - (playInfo.bigBlind || 1)),
            )
          }
        } else {
          opts.onSetHeroStackBb?.(
            stepAlongStops(STACK_BB_STOPS_VIZ, playInfo.heroStackBb, -1),
          )
        }
      }),
      el('span', {
        className: 'tableInfoEditorValue',
        text: stackLabel,
        attrs: { 'aria-live': 'polite' },
      }),
      stepBtn('+', ui.tableVizStepUp, () => {
        if (playInfo.stackUnit === 'chips') {
          const bbStop = stepAlongStops(
            STACK_BB_STOPS_VIZ,
            playInfo.heroStackBb,
            1,
          )
          if (Number.isFinite(playInfo.bigBlind) && playInfo.bigBlind > 0) {
            opts.onSetHeroStackBb?.(bbStop)
          } else {
            opts.onSetHeroStackChips?.(
              playInfo.heroStackChips + (playInfo.bigBlind || 1),
            )
          }
        } else {
          opts.onSetHeroStackBb?.(
            stepAlongStops(STACK_BB_STOPS_VIZ, playInfo.heroStackBb, 1),
          )
        }
      }),
      el('button', {
        className: 'tableInfoDoneBtn',
        text: ui.tableVizEditDone,
        attrs: { type: 'button' },
        on: {
          click: (e) => {
            e.stopPropagation()
            closeInfoEdit(opts.onInfoEditChange)
          },
        },
      }),
    ])
  }

  return el(
    'div',
    {
      className: [
        'tableCenter',
        editable ? 'tableCenterEditable' : '',
        editing ? `tableCenterEditing-${editing}` : '',
      ]
        .filter(Boolean)
        .join(' '),
    },
    [cluster, editor],
  )
}

export function renderTableViz(opts: TableVizOptions): HTMLElement {
  const ui = t()
  const {
    playerCount,
    heroPosition,
    mode,
    activeSeat = 'hero',
    filledVillainSeats,
    heroFilled = false,
    compact = false,
    onSelectSeat,
    playInfo,
    infoEditable = false,
    onSetBlinds,
    onSetPotBb,
    onSetHeroStackBb,
    onSetHeroStackChips,
    onInfoEditChange,
  } = opts

  const layout = seatLayoutPercents(playerCount, heroPosition)
  const count = Math.min(9, Math.max(2, playerCount))
  const shape = tableShapeKind(count)
  const canEditInfo = Boolean(infoEditable && playInfo)

  const seatNodes = layout.map(({ pos, left, top }) => {
    const fullAccess = formatSeatFullAccess(pos, count)
    const code = formatTableSeatLabel(pos, count)
    const isHero = pos === heroPosition
    const isDealer = pos === 'BTN'
    const interactive = isInteractive(mode) && Boolean(onSelectSeat)

    const holeActive =
      mode === 'holePick' &&
      ((isHero && activeSeat === 'hero') || (!isHero && activeSeat === pos))

    const filled =
      (isHero && heroFilled) ||
      (!isHero && Boolean(filledVillainSeats?.has(pos)))

    const isEmpty = !filled && !isHero

    const classes = [
      'tableSeat',
      isHero ? 'tableSeatHero' : 'tableSeatOther',
      isEmpty ? 'tableSeatEmpty' : '',
      filled ? 'tableSeatFilled tableSeatOccupied' : '',
      isDealer ? 'tableSeatDealer' : '',
      mode === 'heroPick' && isHero ? 'tableSeatSelected' : '',
      holeActive ? 'tableSeatHoleActive' : '',
      interactive ? 'tableSeatInteractive' : '',
      compact ? 'tableSeatCompact' : '',
      isHero && playInfo ? 'tableSeatWithStack' : '',
      isHero && canEditInfo ? 'tableSeatStackEditable' : '',
      isHero && canEditInfo && activeInfoEdit === 'stack'
        ? 'tableSeatStackEditing'
        : '',
    ]
      .filter(Boolean)
      .join(' ')

    const stackShort = playInfo
      ? formatHeroStackShort(
          playInfo.heroStackBb,
          playInfo.heroStackChips,
          playInfo.stackUnit,
        )
      : null

    // Soft F: hero seat a11y = seat code first (KO `BTN(나)`, EN `BTN (you)`),
    // never `Hero`. Input UI only — share/export surfaces never render this.
    const heroAria = ui.tableVizHeroSeatAria(formatSeatCode(pos, count))
    const ariaLabel = isHero
      ? stackShort
        ? `${heroAria} · ${stackShort}`
        : heroAria
      : fullAccess

    const codeSpan = el('span', {
      className: 'tableSeatCode',
      text: code,
      attrs: { 'aria-hidden': 'true' },
    })
    // Soft F (UX): hero seat reads 「BTN · 나」 on one line — seat name first,
    // 「나」 in the existing muted tone. No new badge / color / chip.
    // Soft H: shown whenever a Hero seat is set — with a stack, the line sits
    // above the stack badge (「BTN · 나」 / 100bb). Input table only.
    const children: (HTMLElement | null)[] = [
      isHero
        ? el(
            'span',
            { className: 'tableSeatHeroLine', attrs: { 'aria-hidden': 'true' } },
            [
              codeSpan,
              el('span', {
                className: 'tableSeatHeroMe',
                text: ` · ${ui.tableVizHeroBadge}`,
              }),
            ],
          )
        : codeSpan,
    ]
    if (isHero) {
      if (stackShort) {
        // Stack badge is the primary Hero affordance when playInfo is present.
        if (canEditInfo) {
          children.push(
            el('span', {
              className: 'tableSeatStack',
              text: stackShort,
              attrs: {
                'aria-hidden': 'true',
              },
            }),
          )
        } else {
          children.push(
            el('span', {
              className: 'tableSeatStack',
              text: stackShort,
              attrs: { 'aria-hidden': 'true' },
            }),
          )
        }
      }
    } else if (isEmpty) {
      children.push(
        el('span', {
          className: 'tableSeatEmptyMark',
          text: ui.tableVizEmptyMark,
          attrs: { 'aria-hidden': 'true' },
        }),
      )
    }
    if (isDealer) {
      children.push(
        el('span', {
          className: 'tableDealerPip',
          text: ui.tableVizDealer,
          attrs: {
            title: ui.tableVizDealerTitle,
            'aria-label': ui.tableVizDealerTitle,
          },
        }),
      )
    }

    const onSeatClick = () => {
      if (isHero && canEditInfo && playInfo) {
        // Hero tap: edit stack when info-editable; still set Hero in heroPick
        // via onSelectSeat first so position sync stays intact.
        if (interactive) onSelectSeat?.(pos)
        toggleInfoEdit('stack', onInfoEditChange)
        return
      }
      onSelectSeat?.(pos)
    }

    if (interactive || (isHero && canEditInfo)) {
      return el(
        'button',
        {
          className: classes,
          attrs: {
            type: 'button',
            style: `left:${left.toFixed(2)}%;top:${top.toFixed(2)}%`,
            'aria-label':
              isHero && canEditInfo && stackShort
                ? `${heroAria} · ${ui.tableVizStackAria(stackShort)}`
                : ariaLabel,
            'aria-pressed':
              mode === 'heroPick'
                ? isHero
                  ? 'true'
                  : 'false'
                : holeActive
                  ? 'true'
                  : 'false',
            title: isHero ? ariaLabel : fullAccess,
          },
          on: {
            click: onSeatClick,
          },
        },
        children,
      )
    }

    return el(
      'div',
      {
        className: classes,
        attrs: {
          style: `left:${left.toFixed(2)}%;top:${top.toFixed(2)}%`,
          'aria-label': ariaLabel,
          title: isHero ? ariaLabel : fullAccess,
          role: 'img',
        },
      },
      children,
    )
  })

  const felt = el('div', {
    className: 'tableFelt',
    attrs: { 'aria-hidden': 'true' },
  })

  const center = playInfo
    ? renderCenterCluster(playInfo, canEditInfo, {
        onSetBlinds,
        onSetPotBb,
        onSetHeroStackBb,
        onSetHeroStackChips,
        onInfoEditChange,
      })
    : null

  return el(
    'div',
    {
      className: [
        'tableViz',
        compact ? 'tableVizCompact' : 'tableVizFull',
        `tableVizMode-${mode}`,
        `tableVizN${count}`,
        `tableVizShape-${shape}`,
        playInfo ? 'tableVizWithInfo' : '',
        canEditInfo ? 'tableVizInfoEditable' : '',
      ]
        .filter(Boolean)
        .join(' '),
      attrs: {
        role: mode === 'preview' && !canEditInfo ? 'img' : 'group',
        'aria-label': ui.tableVizAria(count),
      },
    },
    [felt, center, ...seatNodes],
  )
}
